import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth-context.js";
import { useTheme } from "../lib/theme.js";
import { IconLogout, IconMenu, IconMoon, IconSun } from "./icons.js";

// Extrai iniciais do displayName para o avatar. Duas letras no máximo:
// mais que isso vira caixa de texto redonda, não avatar.
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return "?";
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? first;
  const a = first[0] ?? "";
  const b = last[0] ?? "";
  return (a + b).toUpperCase();
}

interface TopbarProps {
  /** Abre o drawer da sidebar no mobile. Se ausente, o hambúrguer não aparece. */
  onToggleMenu?: () => void;
  menuOpen?: boolean;
}

export function Topbar({ onToggleMenu, menuOpen = false }: TopbarProps = {}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { resolved, toggle } = useTheme();
  if (!user) return null;

  return (
    <header className="nx-topbar">
      {/* Hambúrguer só é injetado se o shell suportar drawer. Visibilidade   */}
      {/* real fica por conta do CSS (só aparece abaixo de 720px).            */}
      {onToggleMenu && (
        <button
          type="button"
          className="nx-menu-btn"
          onClick={onToggleMenu}
          aria-label={menuOpen ? "Fechar menu" : "Abrir menu"}
          aria-expanded={menuOpen}
          aria-controls="nx-sidebar-drawer"
        >
          <IconMenu />
        </button>
      )}
      <Link to="/app" className="nx-topbar__brand" aria-label="NxVolt — início">
        <img src="/logo-noxxon-dark.png" alt="Noxxon Sat" className="nx-topbar__logo" />
      </Link>

      <div className="nx-topbar__actions">
        <button
          type="button"
          className="nx-icon-btn"
          onClick={toggle}
          aria-label={`Alternar para tema ${resolved === "dark" ? "claro" : "escuro"}`}
          title={`Tema ${resolved === "dark" ? "claro" : "escuro"}`}
        >
          {resolved === "dark" ? <IconSun /> : <IconMoon />}
        </button>

        {user.sso ? (
          <div className="nx-topbar__user" title={user.operadoraNome ?? user.consorcioNome ?? "Noxxon"}>
            <span className="nx-topbar__avatar" aria-hidden="true">
              {initials(user.displayName)}
            </span>
            <span className="nx-topbar__user-meta">
              <span className="nx-topbar__user-name">{user.displayName}</span>
              <span className="nx-topbar__user-role">{user.operadoraNome ?? user.consorcioNome ?? "Noxxon"}</span>
            </span>
          </div>
        ) : (
          <Link
            to="/app/change-password"
            className="nx-topbar__user"
            title="Trocar senha"
          >
            <span className="nx-topbar__avatar" aria-hidden="true">
              {initials(user.displayName)}
            </span>
            <span className="nx-topbar__user-meta">
              <span className="nx-topbar__user-name">{user.displayName}</span>
              <span className="nx-topbar__user-role">{user.role}</span>
            </span>
          </Link>
        )}

        {/* Sair como ícone: em produto usado o dia todo, "Sair" pill grande */}
        {/* pede acidente. Como ícone com hover vermelho, fica presente sem  */}
        {/* competir com o CTA principal da tela.                            */}
        <button
          type="button"
          className="nx-icon-btn nx-icon-btn--danger"
          onClick={() => {
            navigate('/logged-out', { replace: true });
            void logout();
          }}
          aria-label="Sair"
          title="Sair"
        >
          <IconLogout />
        </button>
      </div>
    </header>
  );
}
